import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import KeyboardAltOutlinedIcon from '@mui/icons-material/KeyboardAltOutlined';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import useChat from '../hooks/useChat';
import { getImageQuestionNames } from '../utils/imageQuestions';
import { uploadFormImage } from '../services/ImageUploadService';
import { fetchCallStatus, PhoneContinuationReason } from '../services/PhoneService';
import { useContent } from '../context/ContentContext';
import { useAuth } from '../context/AuthContext';
import MessageItem from '../components/MessageItem';
import DateDivider from '../components/DateDivider';
import TypingIndicator from '../components/TypingIndicator';
import VoicePanel from '../components/VoicePanel';
import useVoiceSession from '../hooks/useVoiceSession';
import QuestionnaireView from '../components/QuestionnaireView';
import useBusiness from '../hooks/useBusiness';
import { BusinessThemeProvider } from '../context/BusinessThemeContext';
import BusinessLogo from '../components/BusinessLogo';
import { BackSvg, MicSvg } from '../components/BusinessIcons';
import { DISPLAY_FONT, FULL_SCREEN_SX, tok } from '../theme/businessTheme';
import PhotoGallery from '../components/PhotoGallery';
import { Message, MessageType } from '../models/Message';
import { isSameDay, timestampToDate } from '../utils/FirestoreUtils';
import SubmissionTracker from '../utils/SubmissionTracker';
import MarkdownText from '../components/MarkdownText';
import AppHandoffSheet from '../components/AppHandoffSheet';
import AppHandoffCard from '../components/AppHandoffCard';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';

/** Handed to FormChatScreen (via router state) when a customer arrives from
    a phone-call continuation link (/c/:token) — see ContinuationScreen. */
export interface CompanionState {
  token: string;
  businessName: string;
  callActive: boolean;
  reason: PhoneContinuationReason;
  reportId?: string;
}

const FormChatScreen: React.FC = () => {
  console.log('=== FormChatScreen: Component initializing ===');
  
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getFormById, loadFormById } = useContent();
  const { userId, loading: authLoading, regenerateUserId } = useAuth();

  // "Companion" mode — set when we arrived from a phone-call continuation
  // link (ContinuationScreen navigates here with router state). Captured
  // once so a later navigation replace (e.g. our own scroll effects) can't
  // lose it.
  const [companion] = useState<CompanionState | undefined>(
    () => (location.state as any)?.companion
  );
  // Times the owner offered (offer link) — handed over by ContinuationScreen.
  const [offeredTimes] = useState<{ start: string; end: string }[]>(
    () => (location.state as any)?.offered ?? []
  );
  const [companionCallActive, setCompanionCallActive] = useState<boolean>(
    !!companion?.callActive
  );

  // Poll call-status while the phone call is live so the banner disappears
  // (and voice re-enables) the moment it ends.
  useEffect(() => {
    if (!companion || !companionCallActive) return;
    const interval = setInterval(async () => {
      try {
        const { callActive } = await fetchCallStatus(companion.token);
        if (!callActive) {
          setCompanionCallActive(false);
        }
      } catch (error) {
        console.error('FormChatScreen: failed to poll phone call status:', error);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [companion, companionCallActive]);
  
  console.log('FormChatScreen: URL params extracted:', { formId });
  console.log('FormChatScreen: Auth state:', { userId });
  
  // Get form from content context
  const form = formId ? getFormById(formId) : undefined;
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const [formLoadAttempted, setFormLoadAttempted] = useState(false);

  // Voice mode ("Scan & Talk"). When the form has voiceEnabled, the customer
  // picks between talking and typing before entering either flow. The hook
  // lives here (not in VoicePanel) so voice.start() runs inside the chooser
  // button's tap gesture — required for audio playback on mobile Safari.
  // null = not chosen yet; typed forms without voice skip the chooser entirely.
  const [chosenMode, setChosenMode] = useState<'voice' | 'typed' | null>(null);
  // Set when voice hands off to chat after a submit — seeds an "anything
  // else?" bubble so the chat doesn't open cold.
  const [voiceHandoff, setVoiceHandoff] = useState(false);
  const voice = useVoiceSession({ formId }, userId);
  // Business branding (public profile + theme). Falls back to defaults.
  const { business } = useBusiness(form?.channelUid);
  const businessName = business.displayName || '';
  // Router state from the business home (/b/:channelId): the Talk button
  // asks for voice straight away, the message button skips the chooser.
  const [startIntent] = useState<'voice' | 'typed' | null>(() => {
    const st = location.state as any;
    if (st?.autoVoice) return 'voice';
    if (st?.startMode === 'typed') return 'typed';
    return null;
  });
  // While a companion phone call is active, the web voice agent stays
  // disabled entirely (no two agents talking to the customer at once).
  // A texted link (session / content / rebook / offer) always lands straight
  // in the existing conversation — never the voice-or-text chooser.
  const [arrivedViaLink] = useState<boolean>(() => !!(location.state as any)?.fromLink || !!companion || startIntent === 'typed');
  const mode: 'chooser' | 'voice' | 'typed' =
    chosenMode ?? (companionCallActive || arrivedViaLink ? 'typed' : form?.voiceEnabled ? 'chooser' : 'typed');

  // Reset loading state when formId changes (for navigation between forms)
  useEffect(() => {
    console.log('FormChatScreen: formId changed, resetting loading state');
    setIsLoadingForm(false);
    setFormLoadAttempted(false);
    setChosenMode(null);
    setVoiceHandoff(false);
    // End any voice call from the previous form so its mic doesn't stay hot
    // after navigating to another form mid-call. voice.stop is stable.
    voice.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, voice.stop]);

  // Talk button on the business home: start voice as soon as the form and
  // the user session are ready (mic permission prompt appears right away).
  const autoVoiceStartedRef = useRef(false);
  useEffect(() => {
    if (startIntent !== 'voice' || autoVoiceStartedRef.current) return;
    if (!form || !userId || !form.voiceEnabled || companionCallActive) return;
    autoVoiceStartedRef.current = true;
    setChosenMode('voice');
    voice.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startIntent, form, userId, companionCallActive]);

  // Log when form loads
  useEffect(() => {
    console.log('FormChatScreen: Form state changed:', {
      formId,
      hasForm: !!form,
      formData: form ? {
        id: form.id,
        title: form.title,
        channelUid: form.channelUid,
        hasQuestions: !!form.questions
      } : null
    });

    if (form) {
      console.log('✅ FormChatScreen: Form successfully loaded');
    } else if (formId) {
      console.log('❌ FormChatScreen: Form not found for ID:', formId);
    } else {
      console.log('⚠️ FormChatScreen: No form ID provided');
    }
  }, [form, formId]);

  // Load form if not in context, or redirect if not found
  useEffect(() => {
    console.log('FormChatScreen: Checking if redirect needed:', {
      hasForm: !!form,
      formId,
      isLoadingForm,
      formLoadAttempted
    });

    if (!form && formId && !isLoadingForm && !formLoadAttempted) {
      console.log('🔄 FormChatScreen: Form not in context, attempting to load it');
      setIsLoadingForm(true);
      setFormLoadAttempted(true);
      loadFormById(formId).then((loadedForm) => {
        console.log('FormChatScreen: loadFormById completed, result:', !!loadedForm);
        setIsLoadingForm(false);
        if (!loadedForm) {
          console.log('❌ FormChatScreen: Failed to load form, redirecting to home');
          navigate('/');
        } else {
          console.log('✅ FormChatScreen: Form loaded successfully, waiting for state update');
          // Form was loaded and added to context, the component will re-render
          // and form will be available from getFormById
        }
      }).catch((error) => {
        console.error('❌ FormChatScreen: Error loading form:', error);
        setIsLoadingForm(false);
        navigate('/');
      });
    } else if (!form && !formId) {
      // No formId in URL at all
      console.log('❌ FormChatScreen: No formId in URL, redirecting to home');
      navigate('/');
    }
  }, [form, navigate, formId, loadFormById, isLoadingForm, formLoadAttempted]);
  
  // Make sure we have a userId
  useEffect(() => {
    console.log('FormChatScreen: Checking userId:', { userId, hasUserId: !!userId });
    
    if (authLoading) {
      console.log('FormChatScreen: Auth still loading, waiting for userId');
      return;
    }

    if (!userId) {
      console.log('🔄 FormChatScreen: No userId found, regenerating...');
      regenerateUserId().catch((error) => {
        console.error('❌ FormChatScreen: Failed to regenerate userId:', error);
      });
    } else {
      console.log('✅ FormChatScreen: UserId available:', userId);
    }
  }, [userId, authLoading, regenerateUserId]);

  // Setup chat hook
  console.log('FormChatScreen: Initializing chat hook with:', {
    hasForm: !!form,
    hasUserId: !!userId,
    formId: form?.id
  });
  
  const {
    messages,
    newMessage,
    isLoading,
    questions,
    updateNewMessage,
    sendMessage,
    removeAllMessages,
  } = useChat(form, userId);

  // App handoff ("Continue in the Paiper app"): sheet + once-per-thread card
  // after the customer's first submission, dismissal remembered per form.
  const [handoffOpen, setHandoffOpen] = useState(false);
  const handoffDismissKey = `appHandoffCardDismissed:${formId}`;
  const [handoffCardDismissed, setHandoffCardDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(handoffDismissKey) === '1';
    } catch {
      return false;
    }
  });
  const dismissHandoffCard = () => {
    setHandoffCardDismissed(true);
    try {
      localStorage.setItem(handoffDismissKey, '1');
    } catch {
      // storage unavailable — dismissal lasts for this visit only
    }
  };
  
  console.log('FormChatScreen: Chat hook initialized:', {
    messagesCount: messages.length,
    isLoading,
    hasNewMessage: !!newMessage,
    questionsCount: Object.keys(questions).length
  });

  // Companion mode — find the latest bubble matching the phone call's
  // "reason" (why the agent sent this link) so we can scroll to / highlight
  // it once the transcript has loaded.
  const highlightedMessageId = useMemo(() => {
    if (!companion || !companion.reason) return null;
    if (companion.reportId) {
      const match = messages.find((m) => m.id === companion.reportId);
      if (match) return match.id;
    }
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (
        companion.reason === 'payment' &&
        (m.messageType === MessageType.PAYMENT || m.messageType === MessageType.PAYMENT_PAID)
      ) {
        return m.id;
      }
      if (companion.reason === 'review' && (m.response === 'yes' || m.response === 'submitted')) {
        return m.id;
      }
      if (companion.reason === 'content' && m.imageMessageUrl) {
        return m.id;
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companion, messages]);

  const scrolledToHighlightRef = useRef(false);
  useEffect(() => {
    if (highlightedMessageId && !scrolledToHighlightRef.current) {
      scrolledToHighlightRef.current = true;
      // Let the messages list paint first.
      setTimeout(() => {
        document
          .getElementById(`msg-${highlightedMessageId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  }, [highlightedMessageId]);

  // UI state
  const [isQuestionsVisible, setIsQuestionsVisible] = useState(false);
  const [showQuestionnaireOverlay, setShowQuestionnaireOverlay] = useState(false);
  const [activeMessage, setActiveMessage] = useState<Message | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showImages, setShowImages] = useState(false);

  // Companion mode — a "content" link means the caller asked to SEE the
  // business's photos/menu on the phone call, so open the gallery once.
  const autoOpenedGalleryRef = useRef(false);
  useEffect(() => {
    if (autoOpenedGalleryRef.current || companion?.reason !== 'content' || !form) return;
    if ((form.imageUrls && form.imageUrls.length > 0) || form.formImageUrl) {
      autoOpenedGalleryRef.current = true;
      setShowImages(true);
    }
  }, [companion, form]);

  // Flat "what we'll ask" list — required first, then optional.
  const questionItems = useMemo(() => {
    const items: { text: string; optional: boolean }[] = [];
    const sections = Object.keys(questions || {}).sort((a, b) => {
      if (a === 'Required' || b === 'Optional') return -1;
      if (b === 'Required' || a === 'Optional') return 1;
      return a.localeCompare(b);
    });
    sections.forEach((section) => {
      const sq = (questions as any)[section];
      if (sq && typeof sq === 'object') {
        Object.keys(sq).sort().forEach((q) => items.push({ text: q, optional: section === 'Optional' }));
      }
    });
    return items;
  }, [questions]);

  // Image-question chat attachments (spec §4a) ------------------------------
  const imageQuestionNames = useMemo(
    () => (form ? getImageQuestionNames(form.questions) : []),
    [form?.questions]
  );
  const hasImageQuestions = imageQuestionNames.length > 0;
  // Questions already photographed via the chat camera button this session —
  // used to pick "the first image question with no staged photo".
  const [answeredImageQuestions, setAnsweredImageQuestions] = useState<Set<string>>(new Set());
  const [imageChooserOptions, setImageChooserOptions] = useState<string[] | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const pendingImageQuestionRef = useRef<string | null>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);

  const openCameraPicker = () => {
    if (uploadingImage) return;
    const unanswered = imageQuestionNames.filter((q) => !answeredImageQuestions.has(q));
    const options = unanswered.length > 0 ? unanswered : imageQuestionNames;
    if (options.length === 0) {
      // Companion mode pins the camera visible even when the form has no
      // (more) image questions — the phone agent may just want a reference
      // photo, so fall back to a generic, question-less attachment.
      if (companion) {
        pendingImageQuestionRef.current = null;
        imageFileInputRef.current?.click();
      }
      return;
    }
    if (options.length === 1) {
      pendingImageQuestionRef.current = options[0];
      imageFileInputRef.current?.click();
    } else {
      setImageChooserOptions(options);
    }
  };

  const chooseImageQuestion = (question: string) => {
    pendingImageQuestionRef.current = question;
    setImageChooserOptions(null);
    imageFileInputRef.current?.click();
  };

  const handleImageFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const question = pendingImageQuestionRef.current;
    // A companion-mode photo may have no associated question at all.
    if (!file || !form || !userId || (!question && !companion)) return;

    setUploadingImage(true);
    try {
      const url = await uploadFormImage({ file, channelUid: form.channelUid, formId: form.id });
      const caption = question ? `📷 Photo attached for: ${question}` : '📷 Photo attached';
      await sendMessage(caption, url);
      if (question) {
        setAnsweredImageQuestions((prev) => {
          const next = new Set(prev);
          next.add(question);
          return next;
        });
      }
    } catch (error) {
      console.error('Error uploading chat photo:', error);
      alert('Sorry, that photo could not be uploaded. Please try again.');
    } finally {
      setUploadingImage(false);
      pendingImageQuestionRef.current = null;
    }
  };

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Handle when new messages arrive
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // Check scroll position to show/hide scroll button
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const isScrolledUp = scrollHeight - scrollTop - clientHeight > 100;
      setShowScrollButton(isScrolledUp);
    }
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle send message
  const handleSendMessage = async () => {
    console.log('handleSendMessage called in FormChatScreen');
    console.log('Conditions:', {
      hasMessage: !!newMessage.trim(),
      hasForm: !!form,
      hasUserId: !!userId
    });
    
    if (form) {
      console.log('Form details:', {
        id: form.id,
        title: form.title,
        channelUid: form.channelUid,
        hasQuestions: !!form.questions
      });
    }
    
    if (newMessage.trim() && form && userId) {
      console.log('All conditions met, calling sendMessage()');
      
      try {
        // Create a local copy of the message text before clearing the input
        const messageToSend = newMessage.trim();
        console.log('Message to send:', messageToSend);
        
        // Show an alert to confirm sending (this helps debug if the button is working)
        // alert(`Sending message: "${messageToSend}"`);
        
        // Clear the input field first to give user feedback
        updateNewMessage('');
        
        // Send the message with the saved text
        console.log('About to call sendMessage with text:', messageToSend);
        await sendMessage(messageToSend);
        console.log('Message sent successfully!');
        
        // Hide the questions section after sending
        console.log('Changing isQuestionsVisible to false');
        setIsQuestionsVisible(false);
        
        // Try to force a re-render by updating a state
        setShowScrollButton(prev => !prev);
        setTimeout(() => setShowScrollButton(prev => !prev), 100);
      } catch (error) {
        console.error('Error in handleSendMessage:', error);
        alert(`Error sending message: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } else {
      console.log('Some conditions not met, not sending message');
      alert(`Cannot send message. HasMessage: ${!!newMessage.trim()}, HasForm: ${!!form}, HasUserId: ${!!userId}`);
    }
  };

  // Handle keypress to send message
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      console.log('Enter key pressed, calling handleSendMessage()');
      handleSendMessage();
    }
  };

  // Handle showing questionnaire
  const handleShowQuestionnaire = (message: Message) => {
    // Check if this message has already been submitted
    const alreadySubmitted = SubmissionTracker.checkIfSubmittedResponseExists(message.id);
    
    if (!alreadySubmitted) {
      setActiveMessage(message);
      setShowQuestionnaireOverlay(true);
    }
  };

  // Show loading state while form or userId is not available
  if (!form || !userId) {
    return (
      <BusinessThemeProvider business={business}>
        <Box sx={{ ...FULL_SCREEN_SX, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: tok('background'), color: tok('onBackground') }}>
          <CircularProgress size={48} aria-label="Loading" sx={{ color: tok('onBackground') }} />
        </Box>
      </BusinessThemeProvider>
    );
  }

  // The form's image is the face of the business/event — shown as a
  // thumbnail in the app bar; tapping it opens the photo gallery.
  const bannerUrl = form.formImageUrl || (form.imageUrls && form.imageUrls[0]) || '';
  const galleryUrls: string[] =
    form.imageUrls && form.imageUrls.length > 0
      ? form.imageUrls
      : bannerUrl
        ? [bannerUrl]
        : [];

  // Structured questions when available, else the raw questions markdown.
  const hasQuestionList = questionItems.length > 0 || !!form.questions;
  const questionList = questionItems.length > 0
    ? <QuestionList items={questionItems} />
    : <MarkdownText markdown={form.questions} />;

  const headerName = businessName || form.title;
  const headerSubtitle = businessName ? form.title : '';
  const canTalk = !!form.voiceEnabled && !companionCallActive;

  return (
    <BusinessThemeProvider business={business}>
    <AppHandoffSheet open={handoffOpen} onClose={() => setHandoffOpen(false)} formId={form.id} agentTitle={form.title} />
    <Box sx={{ ...FULL_SCREEN_SX, display: 'flex', flexDirection: 'column', position: 'relative', background: tok('surface'), color: tok('onSurface') }}>
      {/* Offered-times banner — the business suggested other times. */}
      {offeredTimes.length > 0 && (
        <Box
          sx={{
            width: '100%',
            py: 0.9,
            px: 2,
            textAlign: 'center',
            background: tok('dock'),
            color: tok('onBackground'),
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.01em' }}>
            Offered times:{' '}
            {offeredTimes
              .map((t) => {
                const d = new Date(t.start);
                return isNaN(d.getTime())
                  ? t.start
                  : d.toLocaleString(undefined, {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    });
              })
              .join(' · ')}
            {' '}— tell the assistant which one works.
          </Typography>
        </Box>
      )}

      {/* Companion-mode banner — the customer picked up a phone-call
          continuation link and the call is still live. */}
      {companion && companionCallActive && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 0.9,
            px: 2,
            background: tok('dock'),
            color: tok('onBackground'),
          }}
        >
          <PhoneInTalkIcon sx={{ fontSize: 17 }} />
          <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.01em' }}>
            On the phone with {companion.businessName}
          </Typography>
        </Box>
      )}

      {/* Branded header (board B): back to business home, logo/monogram,
          business name + agent title, Talk (mic) button. Hidden in voice
          mode — the VoicePanel carries its own branded screen. */}
      {mode !== 'voice' && (
        <Box
          component="header"
          sx={{
            flexShrink: 0,
            background: tok('background'),
            color: tok('onBackground'),
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            pt: 'max(10px, env(safe-area-inset-top))',
            pb: 1.25,
          }}
        >
          {business.loaded && (
            <IconButton
              onClick={() => navigate(`/b/${form.channelUid}`)}
              aria-label={`Back to ${businessName || 'business'}`}
              sx={{ width: 40, height: 40, flexShrink: 0, background: tok('secondaryAction'), color: tok('onSecondaryAction'), '&:hover': { background: tok('secondaryAction') } }}
            >
              <BackSvg />
            </IconButton>
          )}
          <BusinessLogo name={headerName} logoUrl={business.logoUrl} size={36} radius="50%" />
          <Box
            component={business.loaded ? 'button' : 'div'}
            onClick={business.loaded ? () => navigate(`/b/${form.channelUid}/about`) : undefined}
            sx={{
              flexGrow: 1,
              minWidth: 0,
              textAlign: 'left',
              background: 'none',
              border: 0,
              p: 0,
              m: 0,
              color: 'inherit',
              font: 'inherit',
              cursor: business.loaded ? 'pointer' : 'default',
            }}
          >
            <Typography component="div" noWrap sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.25 }}>
              {headerName}
            </Typography>
            {headerSubtitle && (
              <Typography component="div" noWrap sx={{ fontSize: 13, color: tok('onBackgroundMuted'), lineHeight: 1.3 }}>
                {headerSubtitle}
              </Typography>
            )}
          </Box>

          {/* Photo button if images exist */}
          {form.imageUrls && form.imageUrls.length > 0 && (
            <Button
              variant="text"
              color="inherit"
              aria-label="View photos"
              startIcon={<PhotoLibraryIcon sx={{ fontSize: 18 }} />}
              onClick={() => setShowImages(true)}
              sx={{
                borderRadius: 999,
                background: tok('secondaryAction'),
                color: tok('onSecondaryAction'),
                px: 1.5,
                minWidth: 0,
                flexShrink: 0,
                '&:hover': { background: tok('secondaryAction') },
              }}
            >
              {form.imageUrls.length}
            </Button>
          )}

          {messages.length > 0 && (
            <IconButton
              onClick={() => setHandoffOpen(true)}
              aria-label="Continue in the app"
              title="Continue in the app"
              sx={{ width: 40, height: 40, flexShrink: 0, background: tok('secondaryAction'), color: tok('onSecondaryAction'), '&:hover': { background: tok('secondaryAction') } }}
            >
              <SmartphoneOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}

          {canTalk && (
            <IconButton
              onClick={() => {
                // start() runs inside this tap (mobile Safari audio).
                setChosenMode('voice');
                voice.start();
              }}
              aria-label="Talk"
              sx={{ width: 40, height: 40, flexShrink: 0, background: tok('action'), color: tok('onAction'), '&:hover': { background: tok('action') } }}
            >
              <MicSvg size={20} />
            </IconButton>
          )}
        </Box>
      )}

      {/* "What we'll ask" — a slim bar in typed mode; tapping it drops the
          list down OVER the chat (scrim behind) instead of pushing the chat
          down. Hidden in voice mode (VoicePanel has its own answer card),
          on the chooser (the chooser lists the questions itself) and after
          a voice handoff. */}
      {mode === 'typed' && !voiceHandoff && hasQuestionList && (
        <Box sx={{ position: 'relative', flexShrink: 0, zIndex: 20 }}>
          <Box
            component="button"
            onClick={() => setIsQuestionsVisible(!isQuestionsVisible)}
            aria-expanded={isQuestionsVisible}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              border: 0,
              borderBottom: `1px solid ${tok('cardBorder')}`,
              background: tok('card'),
              color: tok('onSurface'),
              font: 'inherit',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <ChecklistIcon sx={{ fontSize: 18, color: tok('accent') }} />
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
              What we'll ask
              {questionItems.length > 0 && (
                <Box component="span" sx={{ color: tok('onSurfaceMuted'), fontWeight: 500 }}> · {questionItems.length}</Box>
              )}
            </Typography>
            <ExpandMoreIcon
              sx={{ fontSize: 20, color: tok('onSurfaceMuted'), transition: 'transform 0.2s', transform: isQuestionsVisible ? 'rotate(180deg)' : 'none' }}
            />
          </Box>
          {isQuestionsVisible && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  maxHeight: '55vh',
                  overflowY: 'auto',
                  px: 2,
                  pt: 1,
                  pb: 1.5,
                  background: tok('card'),
                  borderBottom: `1px solid ${tok('cardBorder')}`,
                  boxShadow: '0 12px 24px rgba(0,0,0,0.12)',
                  borderRadius: '0 0 18px 18px',
                }}
              >
                {questionList}
              </Box>
            </>
          )}
        </Box>
      )}
      {mode === 'typed' && isQuestionsVisible && (
        <Box
          onClick={() => setIsQuestionsVisible(false)}
          sx={{ position: 'absolute', inset: 0, zIndex: 15, background: 'rgba(0,0,0,0.25)' }}
        />
      )}

      {/* Mode chooser: talk vs. type (voice-enabled forms only). Scrolls
          as one unit so nothing hides behind the mobile toolbar. */}
      {mode === 'chooser' && (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', backgroundColor: tok('surface') }}>
          <Box sx={{ my: 'auto', px: 2, py: 3, width: '100%', maxWidth: 440, mx: 'auto', boxSizing: 'border-box' }}>
            <Typography
              component="h2"
              sx={{ fontFamily: DISPLAY_FONT, fontWeight: 800, fontSize: 30, lineHeight: 1.1, letterSpacing: '-0.02em', color: tok('onSurface'), textAlign: 'center' }}
            >
              Talk or type?
            </Typography>
            <Typography sx={{ mt: 1, mb: 2.5, fontSize: 15, color: tok('onSurfaceMuted'), textAlign: 'center' }}>
              Whatever's easier — you can switch anytime.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.25 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<MicSvg size={20} />}
                onClick={() => {
                  // start() must run inside this tap gesture (mobile Safari audio).
                  setChosenMode('voice');
                  voice.start();
                }}
                disabled={!userId}
                sx={{
                  borderRadius: 999,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 16,
                  background: tok('action'),
                  color: tok('onAction'),
                  boxShadow: 'none',
                  '&:hover': { background: tok('action'), boxShadow: 'none' },
                }}
              >
                Talk
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={<KeyboardAltOutlinedIcon />}
                onClick={() => setChosenMode('typed')}
                sx={{ borderRadius: 999, py: 1.5, borderWidth: 1.5, textTransform: 'none', fontWeight: 700, fontSize: 16, color: tok('onSurface'), background: tok('card'), borderColor: tok('cardBorder'), '&:hover': { borderWidth: 1.5, borderColor: tok('cardBorder'), background: tok('card') } }}
              >
                Type
              </Button>
            </Box>
            {hasQuestionList && (
              <Box sx={{ mt: 3, p: 2, borderRadius: '18px', background: tok('card'), border: `1px solid ${tok('cardBorder')}` }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tok('onSurfaceMuted'), mb: 1 }}>
                  We'll ask about
                </Typography>
                {questionList}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Voice mode */}
      {mode === 'voice' && (
        <VoicePanel
          form={form}
          voice={voice}
          onExit={() => {
            voice.stop();
            setChosenMode(null);
          }}
          onDoneToChat={() => {
            voice.stop();
            setVoiceHandoff(true);
            setChosenMode('typed');
          }}
        />
      )}

      {/* Typed mode — the existing flow, unchanged */}
      {mode === 'typed' && (
      <>
      {/* Messages area */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          backgroundColor: tok('surface'),
        }}
        onScroll={handleScroll}
      >
        {isLoading && messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : messages.length === 0 && !voiceHandoff ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 72,
                height: 72,
                borderRadius: '24px',
                mb: 2.5,
                backgroundColor: tok('card'),
                border: `1px solid ${tok('cardBorder')}`,
                color: tok('accent'),
              }}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h5" gutterBottom sx={{ color: tok('onSurface'), fontFamily: '"Bricolage Grotesque", "DM Sans", sans-serif', fontWeight: 800 }}>
              Start the conversation
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 340 }}>
              Type a message below and we'll walk you through "{form.title}" together.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxWidth: 900, mx: 'auto', width: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Render messages with date dividers */}
            {messages.reduce<React.ReactNode[]>((nodes, message, index) => {
              const messageDate = timestampToDate(message.timestamp);
              const prevMessage = index > 0 ? messages[index - 1] : null;
              const prevDate = prevMessage ? timestampToDate(prevMessage.timestamp) : null;
              
              const isNewDay = !prevDate || !isSameDay(prevDate, messageDate);
              const isUserMessage = message.fromId === userId;
              
              if (isNewDay) {
                nodes.push(<DateDivider key={`date-${messageDate.toISOString()}`} date={messageDate} />);
              }
              
              // Choice quick-reply chips (spec §4) — only under the MOST
              // RECENT agent message, and only while the customer hasn't
              // replied since (i.e. it's still the last message overall).
              const isLastMessage = index === messages.length - 1;
              const showChoiceChips = isLastMessage && !isUserMessage && !!message.choicePrompt;

              nodes.push(
                <Box
                  key={message.id}
                  id={`msg-${message.id}`}
                  sx={
                    highlightedMessageId === message.id
                      ? {
                          borderRadius: '18px',
                          boxShadow: `0 0 0 3px ${tok('accent')}`,
                          transition: 'box-shadow 0.3s ease',
                        }
                      : undefined
                  }
                >
                  <MessageItem
                    message={message}
                    onShowQuestionnaire={handleShowQuestionnaire}
                    formId={formId}
                    channelId={form?.channelUid}
                    onContinueTransfer={(toFormId) =>
                      navigate(`/form/${toFormId}`, { state: { startMode: 'typed' } })
                    }
                    showChoiceChips={showChoiceChips}
                    onSendChoiceReply={(text) => {
                      sendMessage(text);
                      setIsQuestionsVisible(false);
                    }}
                  />
                </Box>
              );
              
              return nodes;
            }, [])}

            {/* Seeded handoff bubble after a voice submission — invites the
                customer to keep going by chat. */}
            {voiceHandoff && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
                <Box
                  sx={{
                    maxWidth: '80%',
                    px: 2,
                    py: 1.25,
                    borderRadius: '18px',
                    borderBottomLeftRadius: '6px',
                    backgroundColor: tok('businessBubble'),
                    color: tok('onBusinessBubble'),
                  }}
                >
                  <Typography variant="body1">
                    Thanks — your response is in! Anything else I can help with?
                    Just type below.
                  </Typography>
                </Box>
              </Box>
            )}

            {/* App handoff card — once, at the end of the thread, after the
                customer's first submission. */}
            {!handoffCardDismissed &&
              messages.some((m) => m.response === 'submitted') && (
                <AppHandoffCard
                  businessName={businessName || form.title}
                  onContinue={() => setHandoffOpen(true)}
                  onDismiss={dismissHandoffCard}
                />
              )}

            {/* Typing indicator */}
            {isLoading && messages.length > 0 && (
              <TypingIndicator />
            )}
            
            {/* Invisible element for scrolling to bottom */}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 90,
            right: 20,
            zIndex: 10,
          }}
        >
          <IconButton
            onClick={scrollToBottom}
            aria-label="Scroll to latest messages"
            sx={{
              backgroundColor: tok('card'),
              color: tok('onSurface'),
              border: `1px solid ${tok('cardBorder')}`,
              '&:hover': {
                backgroundColor: tok('card'),
              },
            }}
          >
            <KeyboardArrowDownIcon />
          </IconButton>
        </Box>
      )}

      {/* Message input */}
      <Paper
        elevation={0}
        sx={{
          p: 1,
          px: 1.5,
          pb: 'max(10px, env(safe-area-inset-bottom))',
          backgroundColor: tok('surface'),
          borderTop: `1px solid ${tok('cardBorder')}`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1,
            maxWidth: 900,
            mx: 'auto',
          }}
        >
          <input
            ref={imageFileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleImageFileSelected}
          />

          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={newMessage}
            onChange={(e) => updateNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={business.copy.messagePlaceholder}
            inputProps={{ 'aria-label': business.copy.messagePlaceholder }}
            variant="outlined"
            size="small"
            InputProps={{
              // Photo attach lives inside the field (only for agents that
              // ask for photos); the picker offers camera or library.
              startAdornment: (hasImageQuestions || !!companion) ? (
                <IconButton
                  onClick={openCameraPicker}
                  disabled={uploadingImage || !userId}
                  aria-label="Attach a photo"
                  size="small"
                  sx={{ ml: -0.75, mr: 0.5, alignSelf: 'flex-end', mb: '-2px', color: tok('accent') }}
                >
                  {uploadingImage ? <CircularProgress size={18} /> : <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 22 }} />}
                </IconButton>
              ) : undefined,
            }}
            sx={{
              '& textarea::placeholder': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
              '& .MuiOutlinedInput-root': {
                minHeight: 44,
                fontSize: 16,
                borderRadius: '22px',
                backgroundColor: tok('card'),
                color: tok('onSurface'),
                '& fieldset': { borderColor: tok('cardBorder') },
                '&:hover fieldset': { borderColor: tok('cardBorder') },
                '&.Mui-focused fieldset': { borderColor: tok('accent') },
              },
            }}
          />

          <IconButton
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || !userId}
            aria-label="Send message"
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              color: tok('onAction'),
              background: tok('action'),
              '&:hover': {
                background: tok('action'),
              },
              '&.Mui-disabled': {
                background: tok('card'),
                color: tok('onSurfaceMuted'),
                opacity: 0.7,
              },
            }}
          >
            <SendIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Paper>
      </>
      )}

      {/* Full-screen image viewer — same swipeable gallery as voice mode.
          Falls back to the banner image when the form has no photo list. */}
      <PhotoGallery
        open={showImages && galleryUrls.length > 0}
        urls={galleryUrls}
        onClose={() => setShowImages(false)}
      />

      {/* "Which question is this for?" chooser (spec §4a) — shown when more
          than one image question still has no staged photo. */}
      <Dialog open={imageChooserOptions !== null} onClose={() => setImageChooserOptions(null)}>
        <DialogTitle>Which question is this for?</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: 2, minWidth: 260 }}>
          {(imageChooserOptions || []).map((question) => (
            <Button
              key={question}
              variant="outlined"
              onClick={() => chooseImageQuestion(question)}
              sx={{ borderRadius: 2, justifyContent: 'flex-start', textTransform: 'none' }}
            >
              {question}
            </Button>
          ))}
        </DialogContent>
      </Dialog>

      {/* Questionnaire overlay */}
      {showQuestionnaireOverlay && activeMessage && (
        <QuestionnaireView
          message={activeMessage}
          form={form}
          userId={userId}
          onDismiss={() => {
            setShowQuestionnaireOverlay(false);
            setActiveMessage(null);
          }}
        />
      )}
    </Box>
    </BusinessThemeProvider>
  );
};

/** Compact "what we'll ask" list: one line per question, optional ones tagged. */
const QuestionList: React.FC<{ items: { text: string; optional: boolean }[] }> = ({ items }) => (
  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
    {items.map((item) => (
      <Box
        component="li"
        key={item.text}
        sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.5, fontSize: 15, color: tok('onSurface') }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, transform: 'translateY(-2px)', background: item.optional ? tok('cardBorder') : tok('accent') }} />
        <Box sx={{ flex: 1 }}>{item.text}</Box>
        {item.optional && (
          <Box component="span" sx={{ fontSize: 12, color: tok('onSurfaceMuted') }}>optional</Box>
        )}
      </Box>
    ))}
  </Box>
);

export default FormChatScreen;
